// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import OverviewCreatorPromotablePinsGrid from 'app/common/react/components/growth/smb/OverviewCreatorPromotablePinsGrid/OverviewCreatorPromotablePinsGrid';
import ProfileBoards from 'app/common/react/components/profile/ProfileBoards/ProfileBoards';
import ProfileEmptyState from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyState';
import ProfileEmptyStateText from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyStateText';
import ProfileProductOverview from 'app/common/react/components/profile/storefront/ProfileProductOverview/ProfileProductOverview';
import SectionHeader from 'app/common/react/components/profile/SectionHeader/SectionHeader';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { userInQuickPromoteCountry } from 'app/common/lib/BusinessUtil';
import { withRouter } from 'react-router';
import {
  OverviewUserPinsGrid,
  OverviewActivitiesPinsGrid,
} from 'app/common/react/components/profile/UserProfilePinGrid/UserProfilePinGrid';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Container } from 'gestalt';

const LATEST_PIN_LIMIT = 15;
const ACTIVITY_PIN_LIMIT = 9;

export type Props = {
  firstName: string,
  isOwnProfile: boolean,
  isPartner: boolean,
  params: {
    username: string,
  },
  requestContext: RequestContextType,
  user: {
    domain_url: string,
    domain_verified: boolean,
    first_name: string,
    has_catalog: boolean,
    id: string,
    last_pin_save_time: string,
    pin_count: number,
    profile_discovered_public: boolean,
    show_discovered_feed: boolean,
    username: string,
  },
  viewer: Viewer,
};

type State = {
  hasActivityPins: boolean,
};

export class ProfileOverview extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasActivityPins: false,
    };
  }

  seeAllButtonContextLog = (elementType: PCONST.thrift.ElementType) => {
    const { isOwnProfile } = this.props;
    const viewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;
    track(PCONST.thrift.EventType.CLICK, {
      element: elementType,
      view: PCONST.thrift.ViewType.USER,
      viewParameter: viewParam,
    });
  };

  render() {
    const { isOwnProfile, isPartner, user, viewer, requestContext } = this.props;
    const { first_name, id, last_pin_save_time, show_discovered_feed, username } = user;

    const timeSpentViewParam = isOwnProfile
      ? PCONST.thrift.ViewParameterType.USER_SELF
      : PCONST.thrift.ViewParameterType.USER_OTHERS;

    const bizproWebTurBazzarExp = new SingleTreatmentExp(requestContext, 'bizpro_web_tur_bazzar');

    let domainPins = null;
    if (show_discovered_feed) {
      const domainPinsGrid = (
        <OverviewActivitiesPinsGrid
          emptyState={
            <ProfileEmptyState>
              <ProfileEmptyStateText subject="domainPins" surface="profile" user={user} />
            </ProfileEmptyState>
          }
          id={id}
          isOverview
          isOwnProfile={isOwnProfile}
          limit={ACTIVITY_PIN_LIMIT}
          user={user}
          username={username}
        />
      );

      const sublabelText = isOwnProfile
        ? i18n._(
            'Pins people saved from your claimed website and linked accounts',
            'Label for pins saved from a domain belonging to the named individual',
          )
        : i18n._(
            "Pins people saved from {{first_name}}'s website and linked accounts",
            'Label for pins saved from a domain belonging to the named individual',
          );
      const sublabel = interpolateNamedTemplate(sublabelText, {
        first_name,
      });

      domainPins = (
        <Box>
          <Container>
            <SectionHeader
              label={i18n._('Recent activity', 'Label for domain pins on user overview')}
              sublabel={sublabel}
              link={`/${username}/_activities/`}
              onSeeAllButtonClick={() =>
                this.seeAllButtonContextLog(
                  PCONST.thrift.ElementType.USER_OVERVIEW_DISCOVERED_PINS_SEE_ALL_BUTTON,
                )
              }
            />
          </Container>
          <Box justifyContent="center" display="flex">
            <Box maxWidth={820} width="100%">
              {domainPinsGrid}
            </Box>
          </Box>
        </Box>
      );
    }

    const latestProfileBoards = (
      <ProfileBoards
        boardOrder="last_pinned_to"
        firstName={first_name}
        isOverview
        isOwnProfile={isOwnProfile}
        user={user}
        username={username}
      />
    );

    const latestPins = (
      <OverviewUserPinsGrid
        id={id}
        isOverview
        isOwnProfile={isOwnProfile}
        limit={LATEST_PIN_LIMIT}
        user={user}
        username={username}
      />
    );

    const latestPinsContainer = (
      <Box>
        <Container>
          <SectionHeader
            label={i18n._('Latest Pins', 'label for latest pins on overview page')}
            link={`/${username}/pins/`}
            sublabel={
              last_pin_save_time
                ? interpolateNamedTemplate(
                    i18n._('Last saved {{ timeSince }}', 'Timestamp of last updated pin'),
                    { timeSince: i18n.getHumanizedTimesince(last_pin_save_time) },
                  )
                : null
            }
            onSeeAllButtonClick={() =>
              this.seeAllButtonContextLog(
                PCONST.thrift.ElementType.USER_OVERVIEW_LATEST_PINS_SEE_ALL_BUTTON,
              )
            }
          />
        </Container>
        <Box justifyContent="center" display="flex">
          <Box maxWidth={820} width="100%">
            {latestPins}
          </Box>
        </Box>
      </Box>
    );
    const showPromotablePinSection =
      isOwnProfile && isPartner && userInQuickPromoteCountry(viewer, requestContext);

    return (
      <TimeSpent
        view={PCONST.thrift.ViewType.USER}
        viewParameter={timeSpentViewParam}
        component={PCONST.thrift.ComponentType.USER_OVERVIEW}
      >
        <Box>
          {user.has_catalog &&
            ((viewer.isAuth && viewer.username === user.username) ||
              bizproWebTurBazzarExp.enabled()) && (
              <ProfileProductOverview
                user={user}
                username={username}
                viewer={viewer}
                onSeeAllButtonClick={this.seeAllButtonContextLog(
                  PCONST.thrift.ElementType.USER_OVERVIEW_LATEST_PINS_SEE_ALL_BUTTON,
                )}
              />
            )}
          {showPromotablePinSection && (
            <OverviewCreatorPromotablePinsGrid id={id} username={username} />
          )}
          {latestPinsContainer}
          <Container>
            <SectionHeader
              label={i18n._('Latest boards', 'label for latest boards on overview page')}
              link={`/${username}/boards/`}
              onSeeAllButtonClick={() =>
                this.seeAllButtonContextLog(
                  PCONST.thrift.ElementType.USER_OVERVIEW_LATEST_BOARDS_SEE_ALL_BUTTON,
                )
              }
            />
            {latestProfileBoards}
          </Container>
          {domainPins}
        </Box>
      </TimeSpent>
    );
  }
}

const mapStateToProps = (state): {| isPartner: boolean |} => ({
  isPartner: state.viewer.isAuth && state.viewer.isPartner,
});

export default compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(mapStateToProps),
  withRequestContext,
  withRouter,
  withViewer,
)(ProfileOverview);
